import { i18n } from 'i18n'
import { getShapeLabel } from 'type/Inference'
import { comparators, reverse } from 'util/sort'
import { disk } from 'util/typo'

export type BasicSizesRequest = {
  company_id: string
  collection_id: string
  item_id: string
  gender: string
  body_type: string | undefined
  item_type: string | undefined
  height: number
  weight: number
}

export type DetailedSizesRequest = {
  company_id: string
  collection_id: string
  item_id: string
  gender: string
  body_type: string | undefined
  item_type: string | undefined
  height: number | undefined
  shoulders: number | undefined
  bust: number | undefined
  waist: number | undefined
  hips: number | undefined
  leg_length: number | undefined
}

export type SizeMatch = {
  size?: string
  tg?: string | number
  bodyType?: string
  confidence?: 'exact' | 'approximate'
  difference?: number // приходит только при confidence: 'approximate'
  matchPercentage?: number
}

export type SizesResponse = SizeMatch[]

export type Size = {
  shape?: string
  name: string
  percent?: number
}

const KNOWN = ['3XS', 'XXXS', '2XS', 'XXS', 'XS', 'S', 'M', 'L',
  'XL', 'XXL', '2XL', 'XXXL', '3XL', 'XXXXL', '4XL', 'XXXXXL', '5XL']

function compareSizes(left: SizeMatch, right: SizeMatch): number {
  const a = left.tg || 0
  const b = right.tg || 0

  // numbers first
  const an = typeof a === 'number'
  const bn = typeof b === 'number'
  if (an && bn) return a - b
  if (an) return -1
  if (bn) return 1

  const as = a.trim().toUpperCase()
  const bs = b.trim().toUpperCase()

  // known sizes
  const ak = KNOWN.indexOf(as) + 1
  const bk = KNOWN.indexOf(bs) + 1
  if (ak && bk) return ak - bk
  if (ak) return -1
  if (bk) return 1

  return as.localeCompare(bs)
}

function comparePercent(left: SizeMatch, right: SizeMatch): number {
  const a = left.matchPercentage || 0
  const b = right.matchPercentage || 0
  return a - b
}

export function getBestMatch(sizes: SizeMatch[] | undefined): SizeMatch | undefined {
  if (!sizes?.length) return undefined
  const best = [...sizes].sort(reverse(comparators(comparePercent, compareSizes)))
  return best[0]
}

function buildFullName(size?: SizeMatch): string {
  if (!size) return ''
  const tg = size.tg?.toString() || ''
  const sz = size.size?.toString() || ''
  if (!tg) return sz
  if (!sz || tg === sz) return tg
  return tg + ' (' + sz + ')'
}

export function buildSize(dress_shape_type: string | undefined, response: SizesResponse): Size | undefined {
  const best = getBestMatch(response)
  const shape = getShapeLabel(dress_shape_type)
  const main = buildFullName(best)
  const percent = best?.matchPercentage ?? 0

  if (!main) return undefined
  return { shape, name: main, percent }
}

type FormatSizeParams = {
  shape?: boolean
  divider?: string
}

export function formatSize(size: Size, params: FormatSizeParams = {}): string {
  const { name } = size
  const shape = params.shape === false ? '' : size.shape
  const divider = params.divider || ' '
  return [shape, name].filter(s => s).join(divider)
}

export function renderSizes(sizes: Size[], combo: boolean): string {
  const [first, second] = sizes
  const single = first && !second

  if (single) {
    const your = i18n('YourSize')
    const shape = first.shape?.toLowerCase()
    if (combo && shape) return your + ' (' + shape + ')' + ' ' + first.name
    else return your + ' ' + first.name
  }
  return sizes.map(it => formatSize(it)).join(' ' + disk + ' ')
}
